import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Flex,
  Heading,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@chakra-ui/react";
import { t } from "i18next";
import * as React from "react";
import { Fragment, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../../config";

import {
  directEmissionsKeys,
  useAddDirectEmissionsItem,
  useDefaultOrganizationQuestionnaireConfig,
  useDeleteDirectEmissionsItem,
  useFinalizeQuestionnaire,
  useGetDirectEmissionsItems,
  useUpdateDirectEmissionsItem
} from "../../../../hooks/query";
import { useQueryClient } from "react-query";
import { showErrorToast, showSuccessToast } from "../../../../utils";
import { ConfirmationDialog } from "../../../../components/ConfirmationDialog";
import { useDispatch } from "react-redux";
import { hideAppSpinner, showAppSpinner } from "../../../../context/Store/slices/appSpinnerSlice";
import { DimensionItemModal, DimensionsGrid, EmissionsDataGrid } from "../DimensionsGrid";
import { DeleteDialog } from "../../../DataEntryPlanning/components/DeleteDialog";
import { mapEmissionsFormDataToApiData } from "../../helpers";
import { useGetManualEmissionItems } from "../../../../hooks/query/questionnaire/manual-emission-item";

export const directEmissionsConsumptionTypes = [
  "stationaryConsumptionTypes",
  "mobileConsumptionTypes",
  "refrigerantConsumptionTypes"
];

function DirectEmissions({ organizationStructure, defaultValues, isPreview, canFinalize = true, onHasData, onDownloadTemplate, onImport, dataType, methodologyAndSourcesPanel }) {
  const { id: questionnaireId, dimensionItems } = defaultValues;

  const queryClient = useQueryClient();
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleSaveAndClose = () => navigate(routes.dataCollectionDataEntryPlanning);

  const { isOpen: isConfirmDialogOpen, onOpen: onConfirmDialogOpen, onClose: onConfirmDialogClose } = useDisclosure();
  const {
    isOpen: isDimensionItemModalOpen,
    onOpen: onDimensionItemModalOpen,
    onClose: onDimensionItemModalClose
  } = useDisclosure();
  const {
    isOpen: isDeleteItemDialogOpen,
    onOpen: onDeleteItemDialogOpen,
    onClose: onDeleteItemDialogClose
  } = useDisclosure();

  const buttonsWidth = useBreakpointValue({
    base: "24",
    md: "32"
  });

  const STATIONARY_SECTION = "stationary";
  const MOBILE_SECTION = "mobile";
  const REFRIGERANT_SECTION = "refrigerant";

  const [selectedItem, setSelectedItem] = useState(null);
  const [dimensionItemModalData, setDimensionItemModalData] = useState(null);
  const [sectionsSize, setSectionsSize] = useState({});

  const { mutate: finalize } = useFinalizeQuestionnaire({
    ...useDefaultOrganizationQuestionnaireConfig(),
    onError: err => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    }
  });

  const { mutate: addItem } = useAddDirectEmissionsItem({
    onSuccess: () => {
      handleDimensionItemModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.added"));
      queryClient.invalidateQueries(directEmissionsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: updateItem } = useUpdateDirectEmissionsItem({
    onSuccess: () => {
      handleDimensionItemModalClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.updated"));
      queryClient.invalidateQueries(directEmissionsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutate: deleteItem } = useDeleteDirectEmissionsItem({
    onSuccess: () => {
      handleDeleteItemDialogClose();
      showSuccessToast(toast, t("organizationQuestionnaire.page.deleted"));
      queryClient.invalidateQueries(directEmissionsKeys.items());
    },
    onError: err => showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message),
    onSettled: () => {
      dispatch(hideAppSpinner());
    }
  });

  const { mutateAsync: deleteItemAsync } = useDeleteDirectEmissionsItem();

  const handleClearAllItems = async (sectionType, items) => {
    if (!items || items.length === 0) return;
    dispatch(showAppSpinner());
    const results = await Promise.allSettled(
      items.map(item => deleteItemAsync({ questionnaireId, recordId: item.id, type: sectionType }))
    );
    const failed = results.filter(r => r.status === "rejected").length;
    queryClient.invalidateQueries(directEmissionsKeys.items());
    dispatch(hideAppSpinner());
    if (failed === 0) {
      showSuccessToast(toast, `Cleared ${items.length} ${items.length === 1 ? "row" : "rows"}`);
    } else {
      showErrorToast(toast, `Cleared ${items.length - failed} ${items.length - failed === 1 ? "row" : "rows"}, ${failed} failed`);
    }
  };

  const handleFinalizeConfirmation = () => {
    finalize(questionnaireId);
    onConfirmDialogClose();
  };

  const handleDeleteItemClick = (type, item) => {
    onDeleteItemDialogOpen();
    setSelectedItem({ id: item.id, type: type });
  };

  const deleteRecord = () => {
    dispatch(showAppSpinner());
    deleteItem({ questionnaireId, recordId: selectedItem.id, type: selectedItem.type });
  };

  const handleDeleteItemDialogClose = () => {
    setSelectedItem(null);
    onDeleteItemDialogClose();
  };

  const handleEditItemClick = (type, item) => {
    setDimensionItemModalData({
      organizationStructure,
      dimensionItems,
      scope: "one",
      fieldArrayName: type,
      consumptionTypesAttr: directEmissionsConsumptionTypes.find(el => el.includes(type)),
      editData: item
    });
    onDimensionItemModalOpen();
  };

  const handleAddItemClick = (type, sectionType) => {
    setDimensionItemModalData({
      organizationStructure,
      dimensionItems,
      scope: "one",
      fieldArrayName: sectionType,
      consumptionTypesAttr: type
    });
    onDimensionItemModalOpen();
  };

  const handleDimensionItemModalSubmit = (data) => {
    const apiData = mapEmissionsFormDataToApiData(data[dimensionItemModalData.fieldArrayName]);
    dispatch(showAppSpinner());
    if (dimensionItemModalData.editData) {
      updateItem({
        questionnaireId,
        recordId: apiData[0].id,
        data: apiData[0],
        type: dimensionItemModalData.fieldArrayName
      });
    } else {
      addItem({
        questionnaireId,
        data: apiData,
        type: dimensionItemModalData.fieldArrayName
      });
    }
  };

  const handleDimensionItemModalClose = () => {
    setDimensionItemModalData(null);
    onDimensionItemModalClose();
  };

  const handleSectionSizeChange = (section, size) => {
    setSectionsSize({
      ...sectionsSize,
      [section]: size
    });
  };

  const { data: manualEmissionItems = [] } = useGetManualEmissionItems(
    { questionnaireId, scopeCategoryKey: "SCOPE_1" },
    { enabled: isPreview && !!questionnaireId }
  );
  const hasManualEmissions = manualEmissionItems.length > 0;

  const checkForNoActivityData = () => {
    if (organizationStructure) {
      const applicableTypes = directEmissionsConsumptionTypes.filter(type =>
        organizationStructure[type]?.length > 0);
      return isPreview && sectionsSize && Object.keys(sectionsSize).length === applicableTypes.length &&
        typeof Object.keys(sectionsSize).find(key => sectionsSize[key] > 0) === "undefined";
    }
    return false;
  };

  const noActivityData = checkForNoActivityData();
  const noData = noActivityData && !hasManualEmissions;
  useEffect(() => {
    if (onHasData && isPreview) onHasData(!noData);
  }, [noData, onHasData, isPreview]);

  const showActivityAccordion = isPreview || dataType !== "emissions";
  const showManualEmissionsGrid = isPreview || dataType === "emissions";

  return (
    <Fragment>
      {noData ?
        <Box marginBlockStart="10" opacity={0.4} pointerEvents="none">
          <Text fontSize="lg" fontWeight="bold" mb={4}>
            {t("noDataAvailable")}
          </Text>
        </Box> :
        <Box marginBlockStart="10" overflow="auto">
          {showActivityAccordion && organizationStructure && (organizationStructure.stationaryConsumptionTypes?.length > 0 ||
              organizationStructure.mobileConsumptionTypes?.length > 0 ||
              organizationStructure.refrigerantConsumptionTypes.length > 0) &&
            <Accordion defaultIndex={[0]} allowMultiple marginBottom="10">
              {directEmissionsConsumptionTypes.map(type => {
                if (organizationStructure[type]?.length > 0) {
                  const isEmpty = isPreview && sectionsSize && Object.keys(sectionsSize).length > 0 && sectionsSize[type] === 0;
                  const sectionTitleKey = type.split("ConsumptionTypes")[0];
                  let sectionType = "";
                  switch (sectionTitleKey) {
                    case "stationary":
                      sectionType = STATIONARY_SECTION;
                      break;
                    case "mobile":
                      sectionType = MOBILE_SECTION;
                      break;
                    case "refrigerant":
                      sectionType = REFRIGERANT_SECTION;
                      break;
                    default:
                      sectionType = "";
                  }

                  if (isEmpty) {
                    return <Box
                      key={`accordion-item-${type}`}
                      opacity={0.4}
                      py={4}
                      borderTop="1px solid"
                      borderColor="gray.200"
                    >
                      <Text as="h2" fontSize="heading.m" fontWeight="700" color="gray.600">
                        {t(`organizationQuestionnaire.scope1.inputs.${sectionTitleKey}Title`)}
                        <Text as="span" fontSize="sm" fontWeight="normal" color="gray.400" ml={2}>(No data)</Text>
                      </Text>
                    </Box>;
                  }

                  return <AccordionItem
                    key={`accordion-item-${type}`}
                    borderBottom="none"
                  >
                    {
                      () => {
                        return <>
                            <AccordionButton px={0} pt={2} pb={4} _hover={{ bg: "transparent" }}>
                              <Text as="h2" fontSize="heading.m" fontWeight="700" color="gray.600">
                                {t(`organizationQuestionnaire.scope1.inputs.${sectionTitleKey}Title`)}
                              </Text>
                              <AccordionIcon mr={0} ml="auto" boxSize={6} />
                            </AccordionButton>
                          <AccordionPanel px={0} overflow="auto">
                            {questionnaireId && organizationStructure && (
                              <DimensionsGrid
                                questionnaireId={questionnaireId}
                                scope="one"
                                organizationStructure={organizationStructure}
                                sectionType={sectionType}
                                consumptionTypesAttr={type}
                                getItemsFunction={useGetDirectEmissionsItems}
                                handleAddItem={() => handleAddItemClick(type, sectionType)}
                                handleEditItem={(dataItem) => handleEditItemClick(sectionType, dataItem)}
                                handleDeleteItem={(dataItem) => handleDeleteItemClick(sectionType, dataItem)}
                                handleClearAll={(items) => handleClearAllItems(sectionType, items)}
                                isPreview={isPreview}
                                onDataItemsChange={handleSectionSizeChange}
                                onDownloadTemplate={() => onDownloadTemplate(sectionType)}
                                importBtnId={`importScope1${type}Btn`}
                                onImport={(e, importBtnId) => onImport(e, importBtnId, sectionType)}
                              />
                            )}
                          </AccordionPanel>
                        </>;
                      }
                    }
                  </AccordionItem>;
                }
                return null;
              })}
            </Accordion>
          }
          {showManualEmissionsGrid && questionnaireId && organizationStructure && (
            <EmissionsDataGrid
              organizationStructure={organizationStructure}
              dimensionItems={dimensionItems}
              isPreview={isPreview}
              questionnaireId={questionnaireId}
              scopeCategoryKey="SCOPE_1"
            />
          )}
        </Box>
      }
      {methodologyAndSourcesPanel}
      {!isPreview &&
        <Flex
          data-sticky-footer
          direction="row"
          justify="space-between"
          gap={4}
          mt={8}
          position="sticky"
          bottom="0"
          bg="white"
          borderTop="1px solid"
          borderColor="gray.200"
          py={4}
          zIndex="10"
        >
          <Button
            flex={1}
            bg="limeShade.100"
            color="pureBlack"
            _hover={{ bg: "limeShade.200" }}
            onClick={handleSaveAndClose}
          >
            {t("buttons.saveAndClose")}
          </Button>
          <Tooltip label={canFinalize ? "" : "Add data, methodology, or a file before finalising"} placement="top" hasArrow>
            <Button
              flex={1}
              bg="lime"
              color="pureBlack"
              _hover={{ bg: "limeShade.300" }}
              isDisabled={!canFinalize}
              onClick={() => {
                onConfirmDialogOpen();
              }}
            >
              {t("buttons.finalize")}
            </Button>
          </Tooltip>
        </Flex>
      }
      <ConfirmationDialog
        isOpen={isConfirmDialogOpen}
        onClose={onConfirmDialogClose}
        onConfirm={handleFinalizeConfirmation}
        headerText={t("questionnaireFinalizationDialog.header")}
        confirmationText={t("questionnaireFinalizationDialog.question")}
      />
      <DeleteDialog
        isOpen={isDeleteItemDialogOpen}
        onClose={handleDeleteItemDialogClose}
        onDelete={deleteRecord}
        entityName={"Item"}
      />
      {
        isDimensionItemModalOpen && dimensionItemModalData !== null && <DimensionItemModal
          modalData={dimensionItemModalData}
          isOpen={isDimensionItemModalOpen}
          onClose={handleDimensionItemModalClose}
          onSubmit={handleDimensionItemModalSubmit}
          scope="one"
        />
      }
    </Fragment>
  );
}

export default DirectEmissions;
